import React, { useCallback, useEffect } from 'react';
import jwt_decode from "jwt-decode";
import { css } from "@emotion/react";
import { trim } from "lodash";
import { useMutation } from '@apollo/client';
import EmailValidator from 'email-validator';

import { useAppState } from "core/contexts/AppContext";
import useForm, { FieldList } from 'core/hooks/useForm';
import { login, needEmailVerification, socialLogin, SocialLoginType } from 'core/includes/auth';
import errorParser from 'core/includes/errorParser';
import Loading from 'core/Components/Utils/Loading';
import ErrorMessage from 'core/Components/Errors/ErrorMessage';
import TwoColumn from 'core/Components/Form/TwoColumn';
import LabelledField from 'core/Components/Form/LabelledField';
import Input from "core/Components/Form/Input";
import FormActions from "core/Components/Form/FormActions";
import SocialLogin from './components/SocialLogin';
import theme from "theme/index";
import useSetState from "core/hooks/useSetState";
import { NOOP } from "core/constants";
import RoundButton from "core/Components/Buttons/RoundButton";
import { CREATE_USER } from "core/includes/queries";
import Form from "core/Components/Form/Form";
import useUser from "core/hooks/useUser";
import document from "core/includes/document";

type Props = {
    redirectAfterSignUp?: (user: any) => void,
    onLogin?: () => void,
};

type DefaultState = {
    isSubmitting: boolean,
    errorMessage: string | null,
    isVisibleErrorMessage: boolean,
    areRequirementsVisible: boolean,
    socialButtonKey: number,
}

const INITIAL_STATE = {
    isSubmitting: false,
    errorMessage: "The provided details don't seem to be correct. Please try again.",
    isVisibleErrorMessage: false,
    areRequirementsVisible: false,
    socialButtonKey: Math.random(),
}

const MIN_PASSWORD_LENGTH = 10;

const SignUp: React.FC<Props> = ({ onLogin = NOOP }) => {
    const [appState] = useAppState();

    const [saveData] = useMutation(CREATE_USER, {
        context: {
            clientName: "auth",
        }
    });

    const { getAndSaveUser } = useUser();

    const [state, setState] = useSetState<DefaultState>(INITIAL_STATE);
    const { fields, errors, onFieldChange, validate } = useForm({
        fields: {
            firstName: appState?.user?.FirstName ?? '',
            surname: appState?.user?.Surname ?? '',
            email: appState?.user?.Email ?? '',
            password: '',
            repeatPassword: '',
        },
        validator,
    });

    useEffect(() => {
        if (state.isVisibleErrorMessage) {
            setState({ socialButtonKey: Math.random() });
        }
    }, [state.isVisibleErrorMessage, setState]);

    const submitSignUpForm = useCallback(() => {
        setState({ errorMessage: '', isVisibleErrorMessage: false });

        if (validate()) {
            setState({ isSubmitting: true });
            saveData({
                variables: {
                    AuthType: 'password',
                    Email: fields.email,
                    Password: fields.password,
                    RepeatPassword: fields.repeatPassword,
                    FirstName: fields.firstName,
                    Surname: fields.surname,
                }
            }).then(() => {
                login(fields.email, fields.password).then((data: any) => {
                    if (data.status) {
                        getAndSaveUser();
                    }
                    setState({ isSubmitting: false });
                });
            }).catch((error) => {
                setState({ isSubmitting: false, errorMessage: errorParser(error), isVisibleErrorMessage: true });
                setTimeout(() => document.getElementById("errorMessage")?.scrollIntoView());
            });
        }

    }, [setState, validate, saveData, fields, getAndSaveUser]);

    const onSocialSignup = async (socialProfile: any, socialLoginType: SocialLoginType) => {
        setState({ isSubmitting: true });

        try {
            let email: string = '';
            let firstName: string = '';
            let surname: string = '';
            const credential = socialProfile["credential"];
            if (socialLoginType === "google") {
                const decodedData: any = jwt_decode(credential);
                email = decodedData.email;
                firstName = decodedData.given_name;
                surname = decodedData.family_name;
            } else {
                email = socialProfile.email;
            }
            // register account
            await saveData({
                variables: {
                    AuthType: 'social',
                    Email: email,
                    FirstName: firstName,
                    Surname: surname,
                }
            });

            // log the user in
            await socialLogin(
                email,
                credential,
                socialLoginType
            );

            getAndSaveUser();
        } catch (e) {
            setState({ isSubmitting: false, errorMessage: errorParser(e), isVisibleErrorMessage: true });
        }
    };

    const setAreRequirementsVisible = useCallback(() => {
        setState({ areRequirementsVisible: true });
    }, [setState]);

    const needToSignUpAgain = appState?.authorised && needEmailVerification(appState?.user);

    return (
        <div css={authContent}>
            {state.isSubmitting && <Loading onTop overlay overlayColour={"white"} />}
            <div css={leftHeader}>Create Account</div>
            <ErrorMessage
                isVisible={state.isVisibleErrorMessage}
                message={state.errorMessage}
                css={errorMessageStyle}
            />
            <div css={formContainerStyle}>
                <Form onSubmit={submitSignUpForm}>
                    <TwoColumn>
                        <LabelledField name="name" error={errors.firstName} label="First Name">
                            <Input
                                hasError={!!errors.firstName}
                                css={nameInput}
                                label="Type First Name" name="firstName"
                                onChange={onFieldChange}
                                value={fields.firstName}
                            />
                        </LabelledField>
                        <LabelledField name="surname" error={errors.surname} label="Last Name">
                            <Input
                                hasError={!!errors.surname}
                                css={nameInput}
                                label="Type Last Name"
                                name="surname"
                                onChange={onFieldChange}
                                value={fields.surname}
                            />
                        </LabelledField>
                    </TwoColumn>
                    <LabelledField name="email" error={errors.email} label="Email">
                        <Input
                            hasError={!!errors.email}
                            css={emailInput}
                            label="Enter email address"
                            name="email"
                            onChange={onFieldChange}
                            value={fields.email}
                            {...needToSignUpAgain && { autoFocus: true }}
                        />
                    </LabelledField>
                    <LabelledField name="password" error={errors.password} label="Password">
                        <Input
                            hasError={!!errors.password}
                            css={passwordInput}
                            label="Password"
                            type="password"
                            name="password"
                            onChange={onFieldChange}
                            onFocus={setAreRequirementsVisible}
                            value={fields.password}
                        />
                    </LabelledField>
                    {state.areRequirementsVisible && (
                        <div css={passwordRequirements}>
                            <p>Password must:</p>
                            <ul>
                                <li css={passwordRequirement(fields.password, !!errors.password)}>
                                    Have at least {MIN_PASSWORD_LENGTH} characters
                                </li>
                            </ul>
                        </div>
                    )}
                    <LabelledField name="repeatPassword" error={errors.repeatPassword} label="Confirm Password">
                        <Input
                            hasError={!!errors.repeatPassword}
                            css={passwordInput}
                            label="Confirm password"
                            type="password"
                            name="repeatPassword"
                            onChange={onFieldChange}
                            value={fields.repeatPassword}
                        />
                    </LabelledField>
                    <div css={formActions}>
                        <FormActions customCss={customFormActionsStyles}>
                            <RoundButton
                                css={formSubmitButton}
                                type="submit"
                                forceLoading={state.isSubmitting}
                            >
                                Sign Up
                            </RoundButton>
                        </FormActions>
                    </div>
                    <div css={lineContainerStyle}>
                        <div css={lineStyle} />
                        <span css={lineTextStyle}>or</span>
                        <div css={lineStyle} />
                    </div>
                    <SocialLogin
                        socialSignUp={onSocialSignup}
                        isSignUp
                    />
                    {!needToSignUpAgain && (
                        <div css={signInText}>
                            Already have an account? Please <span css={signInStyle} onClick={onLogin}>sign in</span>.
                        </div>
                    )}
                </Form>
            </div>
        </div>
    );
};

const isPasswordLongEnough = (password: string): boolean => (
    trim(password).length >= MIN_PASSWORD_LENGTH
);

const validator: any = (fields: FieldList, isSubmit: boolean = false) => {
    const errors: any = {};

    if (!fields.firstName) {
        errors.firstName = 'Please provide your first name';
    }

    if (!fields.surname) {
        errors.surname = 'Please provide your last name';
    }

    if (!fields.email) {
        errors.email = 'Please provide your email';
    }

    if (fields.email && !EmailValidator.validate(fields.email) && isSubmit) {
        errors.email = 'Please provide a valid email address';
    }

    if (!fields.password) {
        errors.password = 'Please provide a password';
    }

    if (!fields.repeatPassword) {
        errors.repeatPassword = 'Please provide the confirm password';
    }

    if (isSubmit && fields.repeatPassword !== fields.password) {
        errors.repeatPassword = "Your passwords do not match";
    }

    return errors;
};

export default SignUp;

const lineContainerStyle = css`
    display: flex;

    ${theme.breakpoints.up("5xl")} {
        margin: 68px 0;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        margin-bottom: 38px;
    }

    ${theme.breakpoints.between("lg", "xl")} {
        margin-bottom: 28px;
    }

    ${theme.breakpoints.only('md')} {
        margin-bottom: 21px;
    }

    ${theme.breakpoints.only("sm")} {
        margin-bottom: 25px;
    }

    ${theme.breakpoints.down("xs")} {
        margin-bottom: 18px;
    }
`;

const lineTextStyle = css`
    padding: 0 28px;
    margin-top: -8px;
    color: ${theme.colours.silverChalice2};

    ${theme.breakpoints.up("5xl")} {
        font-size: 18px;
        margin-top: -10px;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        font-size: 14px;
    }

    ${theme.breakpoints.down("xl")} {
        font-size: 11px;
        margin-top: -6px;
    }
`;

const lineStyle = css`
    height: 1px;
    flex-grow: 1;
    background: ${theme.colours.silverChalice2};
`;

const authContent = css`
    margin-top: 40px;
    width: 510px;
    position: relative;

    ${theme.breakpoints.down('sm')} {
        width: 100%;
        max-width: 510px;
        margin: 40px auto 0;
    }

    ${theme.breakpoints.down('xs')} {
        padding: 0 20px;
    }
`;

const formContainerStyle = css`
    margin-top: 12px;
`;

const nameInput = css`
    margin-top: 0;
    box-sizing: border-box;
`;

const emailInput = css`
    margin-top: 0;
    box-sizing: border-box;
    width: 100%;
`;

const passwordInput = css`
    margin-top: 0;
    box-sizing: border-box;
    width: 100%;
`;

const passwordRequirements = css`
    margin-bottom: 19px;

    p {
        margin-bottom: 10px;
        font-size: 14px;
    }
`;

const passwordRequirement = (password: string, fieldHasError: boolean) => css`
    font-size: 14px;
    list-style: disc inside;
    color: ${
            (password === '' && !fieldHasError)
                    ? 'black'
                    : isPasswordLongEnough(password) ? theme.colours.red : theme.colours.crimson
    }
`;

const signInText = css`
    font-size: 14px;
    font-weight: 100;
    text-align: center;

    a {
        text-decoration: none;
        color: ${theme.colours.blue['400']}
    }
`;

const formActions = css`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    ${theme.breakpoints.up("5xl")} {
        margin: 63px 0 68px;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        margin: 41px 0 45px;
    }

    ${theme.breakpoints.between("lg", "xl")} {
        margin: 29px 0 34px;
    }

    ${theme.breakpoints.only("md")} {
        margin: 21px 0 26px;
    }

    ${theme.breakpoints.only("sm")} {
        margin: 26px 0 31px;
    }

    ${theme.breakpoints.down("xs")} {
        margin: 19px 0 24px;
    }

    ${theme.breakpoints.down('md')} {
        margin-top: 20px;
        justify-content: center;
    }
`;

const customFormActionsStyles = css`
    width: 100%;
    justify-content: center;
`;

const formSubmitButton = css`
    background-color: ${theme.colours.blue['400']};
    width: 400px;
    border-radius: 45px;
    height: 40px;
    padding: 0;

    ${theme.breakpoints.down('xs')} {
        width: 250px;
    }
`;

const leftHeader = css`
    color: ${theme.colours.curiousBlue};
    font-weight: ${theme.fonts.weights.bold};

    ${theme.breakpoints.up("xxl")} {
        font-size: 50px;
        line-height: 75px;
    }

    ${theme.breakpoints.between("md", "xl")} {
        font-size: 43px;
        line-height: 65px;
    }

    ${theme.breakpoints.down("sm")} {
        font-size: 30px;
        line-height: 45px;
    }
`;

const errorMessageStyle = css`
    margin-top: 0;
    margin-bottom: 16px;
`;

const signInStyle = css`
    display: inline-block;
    color: ${theme.colours.curiousBlue};
    cursor: pointer;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;
